import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Col,
  Collapse,
  Modal,
  Row,
} from "react-bootstrap";
import { ErrorMessage, Formik, Form } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import CustomDataTable from "../../../layouts/DataTables/CustomDataTable";
import TextError from "../../../layouts/Input/TextError";
import DynamicFieldBuilder from "./DynamicFieldBuilder";
import SCRConfirmDialog from "./SCRConfirmDialog";
import {
  createSCRAdjustmentType,
  createSCRAdjustmentTypeDefinition,
  createSCRPaymentType,
  createSCRPaymentTypeDefinition,
  getSCRAdjustmentTypeDefinitions,
  getSCRAdjustmentTypes,
  getSCRPaymentTypeDefinitions,
  getSCRPaymentTypes,
  toggleSCRAdjustmentTypeActive,
  toggleSCRAdjustmentTypeDefinitionActive,
  toggleSCRPaymentTypeActive,
  toggleSCRPaymentTypeDefinitionActive,
  updateSCRAdjustmentType,
  updateSCRAdjustmentTypeDefinition,
  updateSCRPaymentType,
  updateSCRPaymentTypeDefinition,
} from "./SCRSettingsActions";

const emptyType = {
  id: "",
  name: "",
  description: "",
  isActive: true,
  isAddition: false,
  allowsBankDeposit: false,
  allowsMultiTransaction: false,
};

const getDefinitionCount = (row) =>
  row?.definitionCount ?? (row?.definitions ? row.definitions.length : 0);

const getValidationSchema = (rows, currentType, label) =>
  Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2)
      .max(100)
      .test(
        "unique-type-name",
        `${label} already exists for this distributor`,
        (value) => {
          const name = (value || "").trim().toLowerCase();
          if (!name) {
            return true;
          }

          return !(rows || []).some(
            (row) =>
              (row.name || "").trim().toLowerCase() === name &&
              row.id !== currentType.id
          );
        }
      ),
    description: Yup.string().max(500),
  });

const RequiredIndicator = () => (
  <span className="scr-settings__required-indicator" title="Required">
    *
  </span>
);

const StatusPill = ({ isActive }) => (
  <span
    className={`scr-settings__pill ${
      isActive ? "scr-settings__pill--success" : "scr-settings__pill--warning"
    }`}
  >
    <i className={`fas fa-circle${isActive ? "" : "-notch"}`} />
    {isActive ? "Active" : "Inactive"}
  </span>
);

const SegmentedChoice = ({ label, options, value, onChange, disabled }) => (
  <div className="scr-settings__choice-row">
    {label ? <span className="scr-settings__choice-label">{label}</span> : null}
    <div className="scr-settings__segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={`scr-settings__segmented-item ${
            value === option.value ? "is-active" : ""
          }`}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          <i className={`fas ${option.icon}`} aria-hidden="true" />
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  </div>
);

const TogglePill = ({ active, disabled, icon, label, onClick }) => (
  <button
    type="button"
    className={`scr-settings__toggle-pill ${active ? "is-active" : ""}`}
    onClick={onClick}
    disabled={disabled}
  >
    <i className={`fas ${icon}`} aria-hidden="true" />
    <span>{label}</span>
    <i
      className={`fas fa-${active ? "check-circle" : "circle"} scr-settings__toggle-pill-check`}
      aria-hidden="true"
    />
  </button>
);

const OptionalDetailsToggle = ({ expanded, onToggle, hasValue, readOnly }) => {
  if (readOnly) {
    return null;
  }

  return (
    <button
      type="button"
      className="scr-settings__optional-toggle"
      onClick={onToggle}
      aria-expanded={expanded}
    >
      <i
        className={`fas fa-chevron-${expanded ? "down" : "right"}`}
        aria-hidden="true"
      />
      <span>Optional details</span>
      {hasValue ? (
        <span className="scr-settings__optional-badge">Added</span>
      ) : (
        <span className="scr-settings__optional-hint">Description</span>
      )}
    </button>
  );
};

const TruncatedCellText = ({ value, variant = "description", emptyLabel }) => {
  const text = (value || "").trim();
  if (!text) {
    return emptyLabel ? (
      <span className="text-muted fst-italic">{emptyLabel}</span>
    ) : null;
  }

  return (
    <span
      className={`scr-settings__cell-text scr-settings__cell-text--${variant}`}
      title={text}
    >
      {text}
    </span>
  );
};

const SCRTypeManager = (props) => {
  const { id } = useParams();
  const isPaymentType = props.kind === "payment";
  const distributorId = id || props.distributorMainInfo?.id;
  const [modalShow, setModalShow] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentType, setCurrentType] = useState(emptyType);
  const [confirmState, setConfirmState] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);

  const title = isPaymentType
    ? "Non-VizLink Payment Types"
    : "Adjustment Types";
  const typeLabel = isPaymentType ? "Payment Type" : "Adjustment Type";
  const rows = isPaymentType ? props.paymentTypes : props.adjustmentTypes;
  const definitions = isPaymentType
    ? props.paymentTypeDefinitions
    : props.adjustmentTypeDefinitions;

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((row) => row.isActive).length,
      inactive: rows.filter((row) => !row.isActive).length,
      withFields: rows.filter((row) => getDefinitionCount(row) > 0).length,
    }),
    [rows]
  );

  const loadTypes = useCallback(
    (force = false) => {
      if (!distributorId) {
        return Promise.resolve(null);
      }

      if (isPaymentType) {
        return props.getSCRPaymentTypes(distributorId, true, { force });
      }

      return props.getSCRAdjustmentTypes(distributorId, true, { force });
    },
    [distributorId, isPaymentType, props]
  );

  const loadDefinitions = useCallback(
    async (typeId) => {
      if (!typeId) {
        return null;
      }

      if (isPaymentType) {
        return props.getSCRPaymentTypeDefinitions(typeId, true);
      }

      return props.getSCRAdjustmentTypeDefinitions(typeId, true);
    },
    [isPaymentType, props]
  );

  useEffect(() => {
    loadTypes();
  }, [loadTypes, props.kind]);

  const guardDistributor = useCallback(() => {
    if (!distributorId) {
      toast.warn("Please save distributor main information first.");
      return false;
    }

    return true;
  }, [distributorId]);

  const openCreate = useCallback(() => {
    if (!guardDistributor()) {
      return;
    }

    setCurrentType(emptyType);
    setReadOnly(false);
    setIsEdit(false);
    setShowOptionalDetails(false);
    setModalShow(true);
  }, [guardDistributor]);

  const openExisting = useCallback(
    async (row, mode) => {
      setCurrentType({ ...emptyType, ...row });
      setReadOnly(mode === "view");
      setIsEdit(true);
      setShowOptionalDetails(Boolean((row.description || "").trim()));
      await loadDefinitions(row.id);
      setModalShow(true);
    },
    [loadDefinitions]
  );

  const handleConfirm = useCallback(async () => {
    if (!confirmState) {
      return;
    }

    setConfirming(true);
    try {
      if (confirmState.kind === "toggle") {
        const { row } = confirmState;
        if (isPaymentType) {
          await props.toggleSCRPaymentTypeActive(row.id, !row.isActive);
        } else {
          await props.toggleSCRAdjustmentTypeActive(row.id, !row.isActive);
        }
      }

      if (confirmState.kind === "toggleField") {
        const { field } = confirmState;
        const nextActive = field.isActive === false;
        const result = isPaymentType
          ? await props.toggleSCRPaymentTypeDefinitionActive(
              currentType.id,
              field.id,
              nextActive
            )
          : await props.toggleSCRAdjustmentTypeDefinitionActive(
              currentType.id,
              field.id,
              nextActive
            );

        if (result) {
          await loadDefinitions(currentType.id);
        }
      }
    } finally {
      setConfirming(false);
      setConfirmState(null);
    }
  }, [confirmState, currentType.id, isPaymentType, loadDefinitions, props]);

  const handleSubmit = useCallback(
    async (values) => {
      const model = {
        id: values.id || undefined,
        name: values.name.trim(),
        description: values.description || "",
        isActive: values.isActive !== false,
        distributorId,
        ...(isPaymentType
          ? {
            allowsBankDeposit: Boolean(values.allowsBankDeposit),
            allowsMultiTransaction: Boolean(values.allowsMultiTransaction),
          }
          : {
            isAddition: Boolean(values.isAddition),
          }),
      };

      const result = isEdit
        ? isPaymentType
          ? await props.updateSCRPaymentType(values.id, model)
          : await props.updateSCRAdjustmentType(values.id, model)
        : isPaymentType
          ? await props.createSCRPaymentType(model)
          : await props.createSCRAdjustmentType(model);

      if (result) {
        const saved = { ...values, ...result };
        setCurrentType(saved);
        setIsEdit(true);
        if (saved.id) {
          await loadDefinitions(saved.id);
        }
        // Save Changes: close modal. Create Type stays open so fields can be added.
        if (isEdit) {
          setModalShow(false);
        }
      }
    },
    [distributorId, isEdit, isPaymentType, loadDefinitions, props]
  );

  const createDefinition = useCallback(
    async (model) => {
      const result = isPaymentType
        ? await props.createSCRPaymentTypeDefinition(currentType.id, model)
        : await props.createSCRAdjustmentTypeDefinition(currentType.id, model);
      if (result) {
        await loadDefinitions(currentType.id);
      }
      return result;
    },
    [currentType.id, isPaymentType, loadDefinitions, props]
  );

  const updateDefinition = useCallback(
    async (model) => {
      const result = isPaymentType
        ? await props.updateSCRPaymentTypeDefinition(currentType.id, model.id, model)
        : await props.updateSCRAdjustmentTypeDefinition(currentType.id, model.id, model);
      if (result) {
        await loadDefinitions(currentType.id);
      }
      return result;
    },
    [currentType.id, isPaymentType, loadDefinitions, props]
  );

  const toggleDefinitionActive = useCallback(
    (field) => {
      const isActive = field.isActive !== false;
      setConfirmState({
        kind: "toggleField",
        field,
        title: isActive ? "Deactivate Dynamic Field?" : "Activate Dynamic Field?",
        message: isActive
          ? "This field will no longer appear on new SCR transaction forms. Existing saved data is preserved."
          : "This field will appear again on SCR transaction forms for this type.",
        detail: field.label || field.fieldKey,
        confirmLabel: isActive ? "Yes, Deactivate" : "Yes, Activate",
        variant: isActive ? "warning" : "primary",
        icon: isActive ? "fa-ban" : "fa-check-circle",
      });
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        Cell: ({ value }) => (
          <TruncatedCellText value={value} variant="name" />
        ),
      },
      {
        Header: "Description",
        accessor: "description",
        width: 220,
        minWidth: 160,
        maxWidth: 280,
        Cell: ({ value }) => (
          <TruncatedCellText
            value={value}
            variant="description"
            emptyLabel="No description"
          />
        ),
      },
      ...(isPaymentType
        ? [
          {
            Header: "Bank Deposit",
            accessor: "allowsBankDeposit",
            width: 130,
            minWidth: 110,
            maxWidth: 150,
            Cell: ({ value }) => (
              <span
                className={`scr-settings__pill ${
                  value ? "scr-settings__pill--success" : "scr-settings__pill--muted"
                }`}
              >
                {value ? "Allowed" : "Not Allowed"}
              </span>
            ),
          },
          {
            Header: "Multi Transaction",
            accessor: "allowsMultiTransaction",
            width: 140,
            minWidth: 120,
            maxWidth: 160,
            Cell: ({ value }) => (
              <span
                className={`scr-settings__pill ${
                  value ? "scr-settings__pill--info" : "scr-settings__pill--muted"
                }`}
              >
                {value ? "Allowed" : "Single Only"}
              </span>
            ),
          },
        ]
        : [
          {
            Header: "Type",
            accessor: "isAddition",
            width: 130,
            minWidth: 110,
            maxWidth: 150,
            Cell: ({ value }) => (
              <span
                className={`scr-settings__pill ${
                  value ? "scr-settings__pill--success" : "scr-settings__pill--accent"
                }`}
              >
                <i className={`fas fa-${value ? "plus" : "minus"}`} />
                {value ? "Addition" : "Deduction"}
              </span>
            ),
          },
        ]),
      {
        Header: "Dynamic Fields",
        id: "definitionCount",
        accessor: (row) => getDefinitionCount(row),
        width: 110,
        minWidth: 100,
        maxWidth: 130,
        Cell: ({ value }) => (
          <span
            className={`scr-settings__count-badge ${
              value > 0 ? "scr-settings__count-badge--has-fields" : ""
            }`}
          >
            {value ?? 0}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "isActive",
        width: 110,
        minWidth: 95,
        maxWidth: 130,
        Cell: ({ value }) => <StatusPill isActive={value} />,
      },
      {
        Header: "Actions",
        accessor: "id",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        Cell: ({ row }) => (
          <div className="scr-settings__action-group">
            <button
              type="button"
              onClick={() => openExisting(row.original, "view")}
              className="scr-settings__icon-btn scr-settings__icon-btn--view"
              title="View details"
            >
              <i className="fas fa-eye" />
            </button>
            <button
              type="button"
              onClick={() => openExisting(row.original, "edit")}
              className="scr-settings__icon-btn scr-settings__icon-btn--edit"
              title="Edit"
            >
              <i className="fas fa-edit" />
            </button>
            <button
              type="button"
              onClick={() =>
                setConfirmState({
                  kind: "toggle",
                  row: row.original,
                  title: row.original.isActive
                    ? `Deactivate ${typeLabel}?`
                    : `Activate ${typeLabel}?`,
                  message: row.original.isActive
                    ? "This type will be hidden from new SCR transactions until reactivated."
                    : "This type will become available for SCR transactions again.",
                  detail: row.original.name,
                  confirmLabel: row.original.isActive ? "Yes, Deactivate" : "Yes, Activate",
                  variant: row.original.isActive ? "warning" : "success",
                  icon: row.original.isActive ? "fa-toggle-off" : "fa-toggle-on",
                })
              }
              className={`scr-settings__icon-btn scr-settings__icon-btn--toggle ${
                row.original.isActive ? "is-active" : "is-inactive"
              }`}
              title={row.original.isActive ? "Deactivate" : "Activate"}
            >
              <i
                className={
                  row.original.isActive ? "fas fa-toggle-on" : "fas fa-toggle-off"
                }
              />
            </button>
          </div>
        ),
      },
    ],
    [isPaymentType, openExisting, typeLabel]
  );

  const modalModeLabel = readOnly ? "View" : isEdit ? "Edit" : "Add";

  return (
    <>
      <Row className="scr-settings__toolbar align-items-center">
        <Col>
          <div className="scr-settings__toolbar-title">{title}</div>
          <small className="text-muted">
            Manage distributor-level SCR {isPaymentType ? "payment" : "adjustment"}{" "}
            configuration and dynamic form fields.
          </small>
          <div className="scr-settings__stats">
            <span className="scr-settings__stat">
              <i className="fas fa-layer-group" />
              {stats.total} Total
            </span>
            <span className="scr-settings__stat scr-settings__stat--accent">
              <i className="fas fa-check-circle" />
              {stats.active} Active
            </span>
            <span className="scr-settings__stat">
              <i className="fas fa-pause-circle" />
              {stats.inactive} Inactive
            </span>
            <span className="scr-settings__stat">
              <i className="fas fa-list-ul" />
              {stats.withFields} With Fields
            </span>
          </div>
        </Col>
        <Col md="auto" className="mt-3 mt-md-0">
          <Button
            variant="primary"
            className="scr-settings__primary-button"
            onClick={openCreate}
          >
            <i className="fa fa-plus me-1" />
            Add {typeLabel}
          </Button>
        </Col>
      </Row>

      {rows?.length ? (
        <div
          className={`scr-settings__table-card ${
            isPaymentType
              ? "scr-settings__table-card--payment"
              : "scr-settings__table-card--adjustment"
          }`}
        >
          <CustomDataTable
            columns={columns}
            data={rows || []}
            getTrProps={() => null}
            minHeight="280px"
          />
        </div>
      ) : (
        <div className="scr-settings__empty">
          <div className="scr-settings__empty-icon">
            <i
              className={`fas ${isPaymentType ? "fa-money-bill-wave" : "fa-sliders-h"}`}
              aria-hidden="true"
            />
          </div>
          <div className="scr-settings__empty-title">No {typeLabel.toLowerCase()}s yet</div>
          <p className="scr-settings__empty-text">
            Create your first {typeLabel.toLowerCase()} to define how SCR handles{" "}
            {isPaymentType ? "Non-VizLink payments" : "invoice adjustments"} for this
            distributor.
          </p>
          <Button
            variant="primary"
            className="scr-settings__primary-button"
            onClick={openCreate}
          >
            <i className="fa fa-plus me-1" />
            Add {typeLabel}
          </Button>
        </div>
      )}

      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        backdrop="static"
        size="lg"
        className={`scr-settings__type-modal ${
          currentType.id ? "scr-settings__type-modal--expanded" : ""
        }`}
        centered
      >
        <Modal.Header closeButton className="scr-settings__type-modal-header">
          <div className="scr-settings__type-modal-title-wrap">
            <div
              className={`scr-settings__type-modal-icon scr-settings__type-modal-icon--${
                isPaymentType ? "payment" : "adjustment"
              }`}
            >
              <i
                className={`fas ${isPaymentType ? "fa-money-bill-wave" : "fa-sliders-h"}`}
                aria-hidden="true"
              />
            </div>
            <div className="scr-settings__type-modal-heading">
              <div className="scr-settings__type-modal-heading-top">
                <Modal.Title>
                  {modalModeLabel} {typeLabel}
                </Modal.Title>
                {currentType.id ? (
                  <StatusPill isActive={currentType.isActive !== false} />
                ) : null}
              </div>
              {!currentType.id ? (
                <div className="scr-settings__type-modal-subtitle">
                  Quick setup for this distributor
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Header>
        <Formik
          initialValues={currentType}
          enableReinitialize
          validationSchema={getValidationSchema(
            rows,
            currentType,
            isPaymentType ? "Payment type" : "Adjustment type"
          )}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, handleChange, isSubmitting }) => (
            <Form autoComplete="off">
              <Modal.Body className="scr-settings__type-modal-body">
                <div className="scr-settings__form-card">
                  <Row className="g-2">
                    <Col xs={12} className="scr-settings__field-group scr-settings__field-group--compact">
                      <label htmlFor="name">
                        Name
                        <RequiredIndicator />
                      </label>
                      <input
                        id="name"
                        name="name"
                        className="form-control"
                        type="text"
                        autoComplete="off"
                        disabled={readOnly}
                        value={values.name || ""}
                        onChange={handleChange}
                        placeholder={`Enter ${typeLabel.toLowerCase()} name`}
                      />
                      <ErrorMessage component={TextError} name="name" />
                    </Col>

                    {!readOnly ? (
                      <Col xs={12} className="scr-settings__field-group scr-settings__field-group--compact mb-0">
                        <OptionalDetailsToggle
                          expanded={showOptionalDetails}
                          onToggle={() => setShowOptionalDetails((current) => !current)}
                          hasValue={Boolean((values.description || "").trim())}
                          readOnly={readOnly}
                        />
                        <Collapse in={showOptionalDetails}>
                          <div className="scr-settings__optional-panel">
                            <label htmlFor="description">Description</label>
                            <input
                              id="description"
                              name="description"
                              className="form-control"
                              type="text"
                              autoComplete="off"
                              value={values.description || ""}
                              onChange={handleChange}
                              placeholder="Optional note for your team"
                            />
                            <ErrorMessage component={TextError} name="description" />
                          </div>
                        </Collapse>
                      </Col>
                    ) : (values.description || "").trim() ? (
                      <Col xs={12} className="scr-settings__field-group scr-settings__field-group--compact mb-0">
                        <label htmlFor="description">Description</label>
                        <div className="scr-settings__readonly-value">
                          {values.description}
                        </div>
                      </Col>
                    ) : null}
                  </Row>

                  <div className="scr-settings__form-divider" />

                  {isPaymentType ? (
                    <div className="scr-settings__toggle-pills">
                      <TogglePill
                        active={values.allowsBankDeposit}
                        disabled={readOnly}
                        icon="fa-university"
                        label="Bank Deposit"
                        onClick={() =>
                          setFieldValue(
                            "allowsBankDeposit",
                            !values.allowsBankDeposit
                          )
                        }
                      />
                      <TogglePill
                        active={values.allowsMultiTransaction}
                        disabled={readOnly}
                        icon="fa-clone"
                        label="Multi Transaction"
                        onClick={() =>
                          setFieldValue(
                            "allowsMultiTransaction",
                            !values.allowsMultiTransaction
                          )
                        }
                      />
                    </div>
                  ) : (
                    <SegmentedChoice
                      label="Effect on invoice total"
                      options={[
                        { value: true, label: "Addition", icon: "fa-plus" },
                        { value: false, label: "Deduction", icon: "fa-minus" },
                      ]}
                      value={values.isAddition}
                      onChange={(nextValue) => setFieldValue("isAddition", nextValue)}
                      disabled={readOnly}
                    />
                  )}
                </div>

                {currentType.id ? (
                  <div className="scr-settings__form-card scr-settings__form-card--fields mb-0">
                    <DynamicFieldBuilder
                      definitions={definitions || []}
                      readOnly={readOnly}
                      onCreate={createDefinition}
                      onUpdate={updateDefinition}
                      onToggleActive={toggleDefinitionActive}
                    />
                  </div>
                ) : (
                  <div className="scr-settings__hint scr-settings__hint--compact">
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    <span>Save first to add dynamic fields for SCR forms.</span>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer className="scr-settings__modal-footer">
                <Button variant="outline-secondary" onClick={() => setModalShow(false)}>
                  Close
                </Button>
                {!readOnly && (
                  <Button
                    type="submit"
                    variant="primary"
                    className="scr-settings__primary-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Create Type"}
                  </Button>
                )}
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <SCRConfirmDialog
        show={Boolean(confirmState)}
        onHide={() => !confirming && setConfirmState(null)}
        onConfirm={handleConfirm}
        title={confirmState?.title}
        message={confirmState?.message}
        detail={confirmState?.detail}
        confirmLabel={confirmState?.confirmLabel}
        icon={confirmState?.icon}
        variant={confirmState?.variant}
        confirming={confirming}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  distributorMainInfo: state.distributorMainInfo,
  paymentTypes: state.SCRSettings.paymentTypes,
  adjustmentTypes: state.SCRSettings.adjustmentTypes,
  paymentTypeDefinitions: state.SCRSettings.paymentTypeDefinitions,
  adjustmentTypeDefinitions: state.SCRSettings.adjustmentTypeDefinitions,
});

export default connect(mapStateToProps, {
  getSCRPaymentTypes,
  createSCRPaymentType,
  updateSCRPaymentType,
  toggleSCRPaymentTypeActive,
  getSCRPaymentTypeDefinitions,
  createSCRPaymentTypeDefinition,
  updateSCRPaymentTypeDefinition,
  toggleSCRPaymentTypeDefinitionActive,
  getSCRAdjustmentTypes,
  createSCRAdjustmentType,
  updateSCRAdjustmentType,
  toggleSCRAdjustmentTypeActive,
  getSCRAdjustmentTypeDefinitions,
  createSCRAdjustmentTypeDefinition,
  updateSCRAdjustmentTypeDefinition,
  toggleSCRAdjustmentTypeDefinitionActive,
})(SCRTypeManager);
